// @ts-nocheck
/**
 * 订单模拟数据
 * NOTE: 类型与新 order.ts 桥梁文件不匹配，待前端阶段重写
 */

import { 
  Order, 
  OrderItem, 
  OrderStatus, 
  PaymentStatus, 
  ShippingStatus, 
  PaymentMethod,
  OrderStatistics 
} from '@/api/modules/order';
import { randomBetween } from '@/mock/mockData';
import dayjs from 'dayjs';

// 分销商列表
const distributors = [
  { id: '1', name: '北京总代理', code: 'BJ001' },
  { id: '2', name: '上海分销商', code: 'SH002' },
  { id: '3', name: '广州直营店', code: 'GZ003' },
  { id: '4', name: '深圳加盟商', code: 'SZ004' },
  { id: '5', name: '成都代理商', code: 'CD005' },
  { id: '6', name: '武汉分销点', code: 'WH006' },
  { id: '7', name: '西安总代理', code: 'XA007' },
  { id: '8', name: '南京分销商', code: 'NJ008' },
];

// 仓库列表
const warehouses = [
  { id: '1', name: '北京仓库', code: 'WH001' },
  { id: '2', name: '上海仓库', code: 'WH002' },
  { id: '3', name: '广州仓库', code: 'WH003' },
  { id: '4', name: '成都仓库', code: 'WH004' },
];

// 商品列表
const products = [
  { id: '1', name: 'Nike Air Max 270', image: 'https://picsum.photos/60/60?random=1', sku: 'NK001' },
  { id: '2', name: 'Adidas Ultraboost 22', image: 'https://picsum.photos/60/60?random=2', sku: 'AD001' },
  { id: '3', name: 'Puma RS-X³', image: 'https://picsum.photos/60/60?random=3', sku: 'PM001' },
  { id: '4', name: 'New Balance 574', image: 'https://picsum.photos/60/60?random=4', sku: 'NB001' },
  { id: '5', name: 'Converse Chuck 70', image: 'https://picsum.photos/60/60?random=5', sku: 'CV001' },
  { id: '6', name: 'Vans Old Skool', image: 'https://picsum.photos/60/60?random=6', sku: 'VN001' },
];

// 颜色和尺码选项
const colors = ['黑色', '白色', '红色', '蓝色', '灰色', '棕色'];
const sizes = ['35', '36', '37', '38', '39', '40', '41', '42', '43', '44', '45'];

// 生成订单项
const generateOrderItems = (orderId: string, count: number): OrderItem[] => {
  const items: OrderItem[] = [];
  
  for (let i = 0; i < count; i++) {
    const product = products[randomBetween(0, products.length - 1)];
    const color = colors[randomBetween(0, colors.length - 1)];
    const size = sizes[randomBetween(0, sizes.length - 1)];
    const quantity = randomBetween(1, 5);
    const tagPrice = randomBetween(200, 800);
    const unitPrice = tagPrice * (randomBetween(70, 95) / 100); // 7-9.5折
    const amount = unitPrice * quantity;
    
    items.push({
      id: `item-${orderId}-${i + 1}`,
      orderId,
      skuId: `${product.id}-${color}-${size}`,
      skuCode: `${product.sku}-${color.substring(0, 1)}-${size}`,
      skuName: `${product.name} ${color} ${size}码`,
      productName: product.name,
      productImage: product.image,
      styleNumber: product.sku,
      size,
      color,
      quantity,
      unitPrice,
      tagPrice,
      discount: Math.round((unitPrice / tagPrice) * 100),
      amount,
      purchasePrice: unitPrice * 0.7, // 采购价为销售价的70%
      remark: randomBetween(0, 3) === 0 ? '客户指定颜色' : undefined,
    });
  }
  
  return items;
};

// 生成状态日志
const generateStatusLog = (status: OrderStatus, createTime: string) => {
  const logs = [];
  const statusFlow: OrderStatus[] = [
    OrderStatus.DRAFT,
    OrderStatus.SUBMITTED,
    OrderStatus.APPROVED,
    OrderStatus.CONFIRMED,
    OrderStatus.SHIPPED,
    OrderStatus.DELIVERED,
    OrderStatus.COMPLETED
  ];

  const statusNames = {
    [OrderStatus.DRAFT]: '草稿',
    [OrderStatus.SUBMITTED]: '已提交',
    [OrderStatus.APPROVED]: '已审核',
    [OrderStatus.CONFIRMED]: '已确认',
    [OrderStatus.SHIPPED]: '已发货',
    [OrderStatus.DELIVERED]: '已送达',
    [OrderStatus.COMPLETED]: '已完成',
    [OrderStatus.CANCELLED]: '已取消',
  };

  const currentIndex = statusFlow.indexOf(status);
  let currentTime = dayjs(createTime);
  
  for (let i = 0; i <= currentIndex; i++) {
    logs.push({
      id: `log-${i}`,
      status: statusFlow[i],
      statusName: statusNames[statusFlow[i]],
      time: currentTime.format('YYYY-MM-DD HH:mm:ss'),
      operator: '系统管理员',
    });
    
    if (i < currentIndex) {
      currentTime = currentTime.add(randomBetween(1, 48), 'hour');
    }
  }
  
  return logs;
};

// 生成模拟订单
const generateOrder = (index: number): Order => {
  const distributor = distributors[randomBetween(0, distributors.length - 1)];
  const warehouse = warehouses[randomBetween(0, warehouses.length - 1)];
  const createTime = dayjs().subtract(randomBetween(0, 30), 'day').subtract(randomBetween(0, 23), 'hour');
  
  // 使用 const assertion 的枚举，现在应该总是可用的
  const statuses = Object.values(OrderStatus);
  const status = statuses[randomBetween(0, statuses.length - 1)];
  
  const paymentMethods = Object.values(PaymentMethod);
  const paymentMethod = paymentMethods[randomBetween(0, paymentMethods.length - 1)];
  
  const itemCount = randomBetween(1, 5);
  const items = generateOrderItems(`order-${index + 1}`, itemCount);
  
  const totalCount = items.reduce((sum, item) => sum + item.quantity, 0);
  const worthAmount = items.reduce((sum, item) => sum + (item.tagPrice * item.quantity), 0);
  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);
  const discountAmount = worthAmount - totalAmount;
  const shippingAmount = randomBetween(0, 50);
  const actualAmount = totalAmount + shippingAmount;
  
  // 李宁用户姓名
  const customerNames = ['李伟', '王强', '张敏', '刘洋', '陈佳', '杨帆', '周磊', '徐静', '马云飞', '林志强'];
  const customerName = customerNames[randomBetween(0, customerNames.length - 1)];
  
  const provinces = ['北京', '上海', '广东', '浙江', '江苏', '四川', '湖北', '陕西', '山东', '河南'];
  const province = provinces[randomBetween(0, provinces.length - 1)];
  const cities = {
    '北京': '北京市',
    '上海': '上海市',
    '广东': '广州市',
    '浙江': '杭州市',
    '江苏': '南京市',
    '四川': '成都市',
    '湖北': '武汉市',
    '陕西': '西安市',
    '山东': '济南市',
    '河南': '郑州市'
  };
  const city = cities[province as keyof typeof cities];
  const receiverAddress = `${province}省${city}体育大道${randomBetween(1, 999)}号李宁体验店`;
  
  return {
    id: `order-${index + 1}`,
    code: `LN${String(index + 1).padStart(8, '0')}`,
    externalCode: randomBetween(0, 3) === 0 ? `EXT${String(index + 1).padStart(6, '0')}` : undefined,
    distributorId: distributor.id,
    distributorName: distributor.name,
    customerId: `customer-${randomBetween(1, 100)}`,
    customerName,
    customerPhone: `138${String(randomBetween(10000000, 99999999))}`,
    warehouseId: warehouse.id,
    warehouseName: warehouse.name,
    
    // 金额信息
    totalAmount,
    discountAmount,
    shippingAmount,
    actualAmount,
    worthAmount,
    
    // 状态信息
    status,
    paymentStatus: status === OrderStatus.DRAFT ? PaymentStatus.UNPAID : PaymentStatus.PAID,
    shippingStatus: ([OrderStatus.SHIPPED, OrderStatus.DELIVERED, OrderStatus.COMPLETED] as OrderStatus[]).includes(status)
      ? ShippingStatus.SHIPPED
      : ShippingStatus.PENDING,
    paymentMethod,
    
    // 收货信息
    receiverName: customerName,
    receiverPhone: `139${String(randomBetween(10000000, 99999999))}`,
    receiverAddress,
    receiverAddrCode: ['110000', '110100', '110101'],
    receiverAddrDetails: `李宁专卖店${randomBetween(1, 20)}号门店`,
    
    // 物流信息
    deliveryId: randomBetween(0, 2) === 0 ? 'delivery-1' : undefined,
    deliveryName: randomBetween(0, 2) === 0 ? '李宁专业物流' : undefined,
    deliveryCode: ([OrderStatus.SHIPPED, OrderStatus.DELIVERED, OrderStatus.COMPLETED] as OrderStatus[]).includes(status)
      ? `LN${String(randomBetween(100000000000, 999999999999))}`
      : undefined,
    wmsOutboundCode: ([OrderStatus.SHIPPED, OrderStatus.DELIVERED, OrderStatus.COMPLETED] as OrderStatus[]).includes(status)
      ? `LNWMS${String(randomBetween(100000000, 999999999))}`
      : undefined,
    
    // 发票信息
    invoiceType: randomBetween(1, 3), // 1-普通发票 2-专用发票 3-电子发票
    
    // 业务分类
    broadCategoryId: 'category-1',
    broadCategoryName: '李宁运动用品',
    
    // 时间信息
    orderDate: createTime.format('YYYY-MM-DD'),
    createTime: createTime.toISOString(),
    updateTime: createTime.add(randomBetween(1, 24), 'hour').toISOString(),
    paymentTime: status !== OrderStatus.DRAFT ? createTime.add(randomBetween(10, 60), 'minute').toISOString() : undefined,
    shippingTime: ([OrderStatus.SHIPPED, OrderStatus.DELIVERED, OrderStatus.COMPLETED] as OrderStatus[]).includes(status)
      ? createTime.add(randomBetween(1, 3), 'day').toISOString()
      : undefined,
    deliveryTime: ([OrderStatus.DELIVERED, OrderStatus.COMPLETED] as OrderStatus[]).includes(status)
      ? createTime.add(randomBetween(3, 7), 'day').toISOString()
      : undefined,
    
    // 其他信息
    remark: randomBetween(0, 3) === 0 ? '李宁VIP客户，请优先处理' : undefined,
    totalCount,
    hasReturnOrder: randomBetween(0, 10) === 0,
    hasPurchaseOrder: randomBetween(0, 5) === 0,
    
    // 关联数据
    items,
    statusLog: generateStatusLog(status, createTime.toISOString()),
    attachments: randomBetween(0, 3) === 0 ? [
      {
        id: 'attachment-1',
        name: '李宁订单附件.pdf',
        url: 'https://example.com/lining-attachment.pdf',
      }
    ] : undefined,
    
    // 关联订单
    relatedReturnOrders: randomBetween(0, 10) === 0 ? [
      { id: 'return-1', code: 'LNRET001' }
    ] : undefined,
    relatedPurchaseOrders: randomBetween(0, 5) === 0 ? [
      { id: 'purchase-1', code: 'LNPUR001' }
    ] : undefined,
    
    // 创建人信息
    createdUserId: 'user-1',
    createdUserName: '李宁系统管理员',
  };
};

// 延迟生成订单列表，避免模块初始化时的引用错误
const orderList: Order[] = [];

// 延迟初始化函数
const initializeOrderList = () => {
  if (orderList.length === 0) {
    for (let i = 0; i < 200; i++) {
      orderList.push(generateOrder(i));
    }
  }
  return orderList;
};

// 生成统计数据
const generateStatistics = (): OrderStatistics => {
  const orders = initializeOrderList(); // 确保列表已初始化
  const today = dayjs().format('YYYY-MM-DD');
  const todayOrders = orders.filter(order => order.createTime.startsWith(today));
  
  const pendingStatuses: OrderStatus[] = [OrderStatus.DRAFT, OrderStatus.SUBMITTED, OrderStatus.APPROVED];
  const shippedStatuses: OrderStatus[] = [OrderStatus.SHIPPED, OrderStatus.DELIVERED];

  return {
    totalOrders: orders.length,
    totalAmount: orders.reduce((sum, order) => sum + order.actualAmount, 0),
    pendingOrders: orders.filter(order =>
      pendingStatuses.includes(order.status)
    ).length,
    shippedOrders: orders.filter(order =>
      shippedStatuses.includes(order.status)
    ).length,
    completedOrders: orders.filter(order => order.status === OrderStatus.COMPLETED).length,
    cancelledOrders: orders.filter(order => order.status === OrderStatus.CANCELLED).length,
    todayOrders: todayOrders.length,
    todayAmount: todayOrders.reduce((sum, order) => sum + order.actualAmount, 0),
  };
};

// 销售订单状态枚举
export enum SalesOrderStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  PROCESSING = 'processing',
  SHIPPED = 'shipped',
  DELIVERED = 'delivered',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  REFUNDED = 'refunded'
}

// 支付状态枚举
export enum SalesPaymentStatus {
  UNPAID = 'unpaid',
  PARTIAL = 'partial',
  PAID = 'paid',
  REFUNDING = 'refunding',
  REFUNDED = 'refunded'
}

// 物流状态枚举
export enum SalesShippingStatus {
  PENDING = 'pending',
  PREPARING = 'preparing',
  SHIPPED = 'shipped',
  IN_TRANSIT = 'in_transit',
  DELIVERED = 'delivered',
  EXCEPTION = 'exception'
}

// 销售订单接口
export interface SalesOrder {
  id: string;
  orderNo: string;
  customerName: string;
  customerId: string;
  customerType: string;
  orderStatus: SalesOrderStatus;
  paymentStatus: SalesPaymentStatus;
  shippingStatus: SalesShippingStatus;
  totalAmount: number;
  paidAmount: number;
  discountAmount: number;
  shippingFee: number;
  itemCount: number;
  orderDate: string;
  deliveryDate?: string;
  paymentMethod: string;
  shippingMethod: string;
  shippingAddress: string;
  contactPhone: string;
  salesPerson: string;
  remark?: string;
  createdAt: string;
  updatedAt: string;
}

// 销售订单查询参数
export interface SalesOrderQueryParams {
  current?: number;
  pageSize?: number;
  orderNo?: string;
  customerName?: string;
  orderStatus?: SalesOrderStatus;
  paymentStatus?: SalesPaymentStatus;
  shippingStatus?: SalesShippingStatus;
  dateRange?: [string, string];
  salesPerson?: string;
}

// 销售订单统计
export interface SalesOrderStats {
  totalOrders: number;
  pendingOrders: number;
  processingOrders: number;
  completedOrders: number;
  totalAmount: number;
}

// 销售订单模拟数据
export const salesOrderMockData: SalesOrder[] = [
  {
    id: 'order-1',
    orderNo: 'SO-20241215-001',
    customerName: '北京科技有限公司',
    customerId: 'cust-1',
    customerType: 'enterprise',
    orderStatus: SalesOrderStatus.PROCESSING,
    paymentStatus: SalesPaymentStatus.PAID,
    shippingStatus: SalesShippingStatus.SHIPPED,
    totalAmount: 158999.50,
    paidAmount: 158999.50,
    discountAmount: 5000.00,
    shippingFee: 50.00,
    itemCount: 15,
    orderDate: '2024-12-15 09:30:00',
    deliveryDate: '2024-12-18 14:00:00',
    paymentMethod: '银行转账',
    shippingMethod: '顺丰速运',
    shippingAddress: '北京市朝阳区建国门外大街1号国贸大厦A座18层',
    contactPhone: '13800138001',
    salesPerson: '张三',
    remark: '客户要求尽快发货',
    createdAt: '2024-12-15 09:00:00',
    updatedAt: '2024-12-15 15:30:00',
  },
  {
    id: 'order-2',
    orderNo: 'SO-20241215-002',
    customerName: '上海贸易公司',
    customerId: 'cust-2',
    customerType: 'enterprise',
    orderStatus: SalesOrderStatus.PENDING,
    paymentStatus: SalesPaymentStatus.UNPAID,
    shippingStatus: SalesShippingStatus.PENDING,
    totalAmount: 89500.00,
    paidAmount: 0.00,
    discountAmount: 1500.00,
    shippingFee: 100.00,
    itemCount: 8,
    orderDate: '2024-12-15 14:20:00',
    paymentMethod: '在线支付',
    shippingMethod: '德邦物流',
    shippingAddress: '上海市浦东新区陆家嘴环路1000号恒生银行大厦32层',
    contactPhone: '13900139002',
    salesPerson: '李四',
    remark: '需要开具增值税专用发票',
    createdAt: '2024-12-15 14:00:00',
    updatedAt: '2024-12-15 14:20:00',
  },
  {
    id: 'order-3',
    orderNo: 'SO-20241216-001',
    customerName: '广州电子科技有限公司',
    customerId: 'cust-3',
    customerType: 'enterprise',
    orderStatus: SalesOrderStatus.CONFIRMED,
    paymentStatus: SalesPaymentStatus.PARTIAL,
    shippingStatus: SalesShippingStatus.PREPARING,
    totalAmount: 67890.50,
    paidAmount: 30000.00,
    discountAmount: 1000.00,
    shippingFee: 0.00,
    itemCount: 12,
    orderDate: '2024-12-16 10:15:00',
    paymentMethod: '分期付款',
    shippingMethod: '自提',
    shippingAddress: '广州市天河区天河路385号太古汇办公楼1501室',
    contactPhone: '13600136003',
    salesPerson: '王五',
    remark: '第一期款项已支付',
    createdAt: '2024-12-16 10:00:00',
    updatedAt: '2024-12-16 11:30:00',
  },
  {
    id: 'order-4',
    orderNo: 'SO-20241217-001',
    customerName: '深圳创新科技',
    customerId: 'cust-4',
    customerType: 'enterprise',
    orderStatus: SalesOrderStatus.COMPLETED,
    paymentStatus: SalesPaymentStatus.PAID,
    shippingStatus: SalesShippingStatus.DELIVERED,
    totalAmount: 245680.00,
    paidAmount: 245680.00,
    discountAmount: 10000.00,
    shippingFee: 200.00,
    itemCount: 30,
    orderDate: '2024-12-17 08:45:00',
    deliveryDate: '2024-12-19 09:30:00',
    paymentMethod: '银行转账',
    shippingMethod: '顺丰速运',
    shippingAddress: '深圳市南山区科技园南区高新南四道9号中国花园大厦A座12层',
    contactPhone: '13700137004',
    salesPerson: '赵六',
    createdAt: '2024-12-17 08:30:00',
    updatedAt: '2024-12-20 10:15:00',
  },
  {
    id: 'order-5',
    orderNo: 'SO-20241217-002',
    customerName: '武汉网络科技',
    customerId: 'cust-5',
    customerType: 'enterprise',
    orderStatus: SalesOrderStatus.CANCELLED,
    paymentStatus: SalesPaymentStatus.REFUNDED,
    shippingStatus: SalesShippingStatus.PENDING,
    totalAmount: 56780.00,
    paidAmount: 0.00,
    discountAmount: 2000.00,
    shippingFee: 100.00,
    itemCount: 7,
    orderDate: '2024-12-17 14:30:00',
    paymentMethod: '在线支付',
    shippingMethod: '中通快递',
    shippingAddress: '武汉市洪山区光谷大道现代世贸中心B座18层',
    contactPhone: '13800138005',
    salesPerson: '张三',
    remark: '客户原因取消订单',
    createdAt: '2024-12-17 14:00:00',
    updatedAt: '2024-12-17 16:45:00',
  },
];

// 获取销售订单统计数据
export function getSalesOrderStats(): SalesOrderStats {
  return {
    totalOrders: salesOrderMockData.length,
    pendingOrders: salesOrderMockData.filter(item => item.orderStatus === SalesOrderStatus.PENDING).length,
    processingOrders: salesOrderMockData.filter(item => item.orderStatus === SalesOrderStatus.PROCESSING).length,
    completedOrders: salesOrderMockData.filter(item => item.orderStatus === SalesOrderStatus.COMPLETED).length,
    totalAmount: salesOrderMockData.reduce((sum, item) => sum + item.totalAmount, 0),
  };
}

// 查询销售订单列表
export function querySalesOrders(params: SalesOrderQueryParams): {
  data: SalesOrder[];
  total: number;
} {
  let result = [...salesOrderMockData];
  
  // 根据查询条件过滤
  if (params.orderNo) {
    result = result.filter(item => item.orderNo.includes(params.orderNo!));
  }
  
  if (params.customerName) {
    result = result.filter(item => item.customerName.includes(params.customerName!));
  }
  
  if (params.orderStatus) {
    result = result.filter(item => item.orderStatus === params.orderStatus);
  }
  
  if (params.paymentStatus) {
    result = result.filter(item => item.paymentStatus === params.paymentStatus);
  }
  
  if (params.shippingStatus) {
    result = result.filter(item => item.shippingStatus === params.shippingStatus);
  }
  
  if (params.salesPerson) {
    result = result.filter(item => item.salesPerson === params.salesPerson);
  }
  
  if (params.dateRange && params.dateRange[0] && params.dateRange[1]) {
    const startDate = new Date(params.dateRange[0]).getTime();
    const endDate = new Date(params.dateRange[1]).getTime();
    
    result = result.filter(item => {
      const orderDate = new Date(item.orderDate).getTime();
      return orderDate >= startDate && orderDate <= endDate;
    });
  }
  
  // 分页
  const total = result.length;
  if (params.current && params.pageSize) {
    const start = (params.current - 1) * params.pageSize;
    const end = start + params.pageSize;
    result = result.slice(start, end);
  }
  
  return {
    data: result,
    total
  };
}
